import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { GatewayUser } from '../lib/types';
import { relativeTime } from '../lib/format';
import { Badge, Card, Empty, Notice, PageHead } from '../components/ui';

interface SyncResult {
  users: { seen: number; created: number; updated: number };
  groups: {
    groups: { seen: number; created: number; updated: number; removed: number };
    memberships: { groups: number; granted: number; revoked: number; skipped: number };
  };
  modules: Array<{ moduleKey: string; checked: number; ok: number; problems: number }>;
}

export function Users() {
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const users = useAsync(
    () => api.get<GatewayUser[]>(`/api/admin/users?search=${encodeURIComponent(search)}`),
    [search],
  );

  async function syncDirectory(): Promise<void> {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const result = await api.post<SyncResult>('/api/admin/users/sync');
      const open = result.modules.reduce((total, module) => total + module.problems, 0);
      const roles = result.groups.memberships;
      setSyncResult(
        `${result.users.created} added, ${result.users.updated} refreshed out of ${result.users.seen} in the directory.` +
          ` ${result.groups.groups.seen} groups cached.` +
          (roles.granted || roles.revoked
            ? ` ${roles.granted} role assignments added, ${roles.revoked} withdrawn.`
            : '') +
          (roles.skipped ? ` ${roles.skipped} groups could not be read.` : '') +
          (result.modules.length ? ` ${open} identity mappings still open.` : ''),
      );
      users.reload();
    } catch (cause) {
      setSyncError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <>
      <PageHead
        title="Users"
        description="Everyone imported from Entra ID or who has signed in once. Roles come from Entra groups; individual additional permissions and revocations are maintained per person."
        actions={
          <button className="primary" onClick={() => void syncDirectory()} disabled={syncing}>
            {syncing ? 'Importing...' : 'Import from Entra ID'}
          </button>
        }
      />

      {syncResult ? <Notice tone="ok">{syncResult}</Notice> : null}
      {syncError ? (
        <Notice tone="danger" title="Import failed">
          {syncError}
        </Notice>
      ) : null}

      {users.error ? <Notice tone="danger">{users.error}</Notice> : null}

      <Card tight>
        <div className="matrix-toolbar">
          <input
            type="search"
            placeholder="Name or e-mail"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <span className="muted">{users.data?.length ?? 0} people</span>
        </div>
        {users.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>E-mail</th>
                <th>Permissions</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((user) => (
                <tr key={user.id}>
                  <td>
                    <Link to={`/users/${user.id}`}>{user.display_name ?? user.email ?? user.id}</Link>
                  </td>
                  <td className="muted">{user.email}</td>
                  <td>{user.is_admin ? <Badge tone="accent">Administrator</Badge> : <span className="muted">User</span>}</td>
                  <td className="muted nowrap">{relativeTime(user.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>
            Nobody here yet. "Import from Entra ID" brings everyone in, so roles and identity
            mappings can be prepared before the first sign-in.
          </Empty>
        )}
      </Card>
    </>
  );
}
